import {
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import styles from './OtpInput.module.css';

export interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  disabled?: boolean;
  /** Accessible label for the input group. This component has no i18n of
   * its own — pass a translated string if the host app isn't English-only.
   * Defaults to the English copy so existing callers don't break. */
  groupLabel?: string;
  /** Accessible label for one digit box, given its 1-based index and the
   * total length. Same caveat as `groupLabel`. */
  digitLabel?: (index: number, total: number) => string;
  /** Called when Enter is pressed in any box — the boxes aren't inside a
   * `<form>`, so nothing submits on Enter unless the host wires this up. */
  onEnter?: () => void;
}

export function OtpInput({
  length = 6,
  value,
  onChange,
  error = false,
  disabled = false,
  groupLabel = 'One-time verification code',
  digitLabel = (index, total) => `Digit ${index} of ${total}`,
  onEnter,
}: OtpInputProps) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  // Owns all first-box focus: the initial mount (value starts as ''), and
  // every later reset after a failed verify or a successful resend — so
  // there's a single place this happens instead of also relying on the
  // input's own `autoFocus`.
  useEffect(() => {
    if (value === '') {
      inputRefs.current[0]?.focus();
    }
  }, [value]);

  function setDigit(index: number, digit: string) {
    const next = digits.slice();
    next[index] = digit;
    onChange(next.join(''));
  }

  function handleChange(index: number, rawValue: string) {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    setDigit(index, digit);
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setDigit(index - 1, '');
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      onEnter?.();
    }
  }

  function handlePaste(index: number, e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted.slice(0, length));
    const nextFocusIndex = Math.min(index + pasted.length, length - 1);
    inputRefs.current[nextFocusIndex]?.focus();
  }

  return (
    <div className={styles.wrap} role="group" aria-label={groupLabel}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digit}
          disabled={disabled}
          aria-label={digitLabel(index + 1, length)}
          aria-invalid={error || undefined}
          className={`otp-input-box ${styles.box}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
        />
      ))}
    </div>
  );
}
